import { darfAufgabeAnlegen } from "@/lib/aufgabeVerknuepfung"
import { MailIdMarke } from "@/components/portal/MailIdMarke"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useParams, useSearchParams, Link } from "react-router-dom"
import { ArrowLeft, Paperclip, RefreshCw, ClipboardCheck } from "lucide-react"
import { api } from "@/lib/api"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { StatusBadge } from "@/components/portal/StatusBadge"
import { useState } from "react"
import { StatusActions } from "@/components/portal/StatusActions"
import { AttachmentsSection } from "@/components/portal/AttachmentsSection"
import { EmailBody } from "@/components/portal/EmailBody"
import { InhaltEntferntHinweis } from "@/components/portal/InhaltEntfernt"
import { ReplyForm } from "@/components/portal/ReplyForm"
import { ForwardForm } from "@/components/portal/ForwardForm"
import { toast } from "@/components/portal/Toast"
import { HeroLinkButton, HeroLinkModal } from "@/components/portal/HeroLinkModal"
import { HistoryContextBadge } from "@/components/portal/HistoryContextBadge"
import { BookingModal } from "@/components/portal/BookingModal"
import { terminBlockerFragen } from "@/lib/terminBlocker"
import { stripAttachmentDumps } from "@/lib/utils"
import { CalendarClock } from "lucide-react"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { OffenerAnrufHinweis } from "@/components/portal/OffenerAnrufHinweis"
import { LetzterKontakt } from "@/components/portal/LetzterKontakt"
import type { EmailAntwort } from "@/lib/emailQuery"

export default function EmailDetail() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const vonSuche = params.get("von") === "suche"
  // Suchbegriff und Auswahl mit zurueckgeben, damit die Trefferliste wieder so
  // dasteht wie vorher — sonst landet man auf einer leeren Suchseite.
  const suchZustand = (() => {
    const p = new URLSearchParams()
    for (const schluessel of ["q", "contact_id", "project_id", "phone"]) {
      const wert = params.get(schluessel)
      if (wert) p.set(schluessel, wert)
    }
    const s = p.toString()
    return s ? `?${s}` : ""
  })()
  const [heroOpen, setHeroOpen] = useState(false)
  const [bookOpen, setBookOpen] = useState(false)
  // Termin aus der gerade verschickten Antwort (#19) — schlaegt den
  // `proposed_slot` der eingegangenen Mail.
  const [blockerSlot, setBlockerSlot] = useState<string | null>(null)
  // Keine eigene `queryFn` — siehe `lib/emailQuery.ts`.
  const { data, isLoading, error } = useQuery<EmailAntwort>({
    queryKey: ["email", id],
    enabled: !!id,
  })

  if (isLoading) return <Loading label="Lädt E-Mail..." />
  if (error) return <div className="p-10"><ErrorBox error={error} /></div>
  if (!data) return null

  const e = data.email
  // HERO-Bezeichnung des Vorgangs aus dem lokalen Projekt-Cache — von Hand
  // ergaenzt, weil `api-types.ts` generiert ist.
  const vorgang = e as typeof e & { hero_project_nr?: string | null; hero_project_title?: string | null }

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      {/* Zurueck dorthin, wo man hergekommen ist. Wer aus der Suche kommt, will
          in seine Trefferliste zurueck — nicht in den Posteingang. Der Weg
          steht in `?von=` (das Popup setzt ihn beim Sprung in die volle
          Ansicht). */}
      <Link
        to={vonSuche ? `/projekte${suchZustand}` : "/emails"}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {vonSuche ? "Zurück zur Suche" : "Zurück zur E-Mail-Liste"}
      </Link>

      <div className="rounded-lg border bg-card shadow-xs overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-secondary">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-[18px] font-bold leading-snug" data-pii>{e.subject || "(ohne Betreff)"}</h1>
              <div className="text-[13px] text-muted-foreground mt-1.5 space-y-0.5">
                <div><strong>Von:</strong> <span data-pii>{e.sender}</span></div>
                {e.recipient && <div><strong>An:</strong> <span data-pii>{e.recipient}</span></div>}
                {/* CC lag immer schon in der Antwort, wurde aber nie gezeigt.
                    Das ist keine Kosmetik: der Handlungsbedarf verweist teils
                    ausdruecklich auf "die im CC enthaltenen Eigentuemer" — wer
                    nur im Portal arbeitet, sah nicht, wer das ist. */}
                {e.cc && <div><strong>CC:</strong> <span data-pii>{e.cc}</span></div>}
                <div><strong>Datum:</strong> {fmtRelative(e.received_at)} · Ordner: {e.imap_folder}</div>
                {/* #155: Mail-ID fuer Screenshot und „Problem melden". */}
                <div><MailIdMarke id={e.id} /></div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <StatusBadge status={e.status} />
              <StatusActions
                current={e.status}
                options={["new", "reviewed", "done"]}
                labels={{ new: "Neu", reviewed: "Gesichtet", done: "Erledigt" }}
                mutate={async (s) => {
                  const { error } = await api.PATCH("/api/v1/emails/{email_id}", {
                    params: { path: { email_id: Number(id) } },
                    body: { status: s },
                  })
                  if (error) throw new Error(String(error))
                }}
                invalidateKeys={[["emails"], ["email", id]]}
                size="sm"
              />
              {e.has_attachments && (
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Paperclip className="w-3 h-3" /> Anhänge
                </span>
              )}
            </div>
          </div>

          {/* HERO-Verknuepfung */}
          <div className="mt-3 flex gap-2 flex-wrap items-center">
            <HeroLinkButton
              onClick={() => setHeroOpen(true)}
              linked={!!e.hero_contact_id}
              contactName={e.hero_contact_name}
            />
            {e.hero_contact_id && (
              <a
                href={`/admin/open-hero/contact/${e.hero_contact_id}?tenant=${e.tenant_slug}`}
                target="_blank"
                className="text-[11.5px] text-muted-foreground hover:text-primary"
              >
                → HERO Kontakt öffnen
              </a>
            )}
            {e.hero_project_id && (
              <a
                href={`/admin/open-hero/project/${e.hero_project_id}?tenant=${e.tenant_slug}`}
                target="_blank"
                className="text-[11.5px] text-muted-foreground hover:text-primary"
                title="Vorgang in HERO öffnen"
              >
                → <HeroVorgangName
                  tenantSlug={e.tenant_slug}
                  projectId={e.hero_project_id}
                  nr={vorgang.hero_project_nr}
                  titel={vorgang.hero_project_title}
                  fallback="Vorgang in HERO öffnen"
                />
              </a>
            )}
            <button
              onClick={() => setBookOpen(true)}
              disabled={!e.hero_contact_id || !!e.hero_booking_id}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
              title={
                e.hero_booking_id ? "Bereits gebucht" :
                !e.hero_contact_id ? "Erst HERO-Kontakt verknüpfen" :
                "Termin anlegen"
              }
            >
              <CalendarClock className="w-3.5 h-3.5" />
              {e.hero_booking_id ? "Gebucht" : "Termin anlegen"}
            </button>
          </div>
          {/* #86: Rückmeldung zu einem offenen Telefonat */}
          <OffenerAnrufHinweis anruf={e.offener_anruf} emailId={Number(id)} />
          {/* #162: letzter Kontakt zum Kunden + Telefonat vermerken */}
          <LetzterKontakt emailId={Number(id)} />
          <HeroLinkModal
            open={heroOpen}
            onClose={() => setHeroOpen(false)}
            mode="email"
            itemId={Number(id)}
            tenantSlug={e.tenant_slug}
            currentContactId={e.hero_contact_id}
            currentContactName={e.hero_contact_name}
            currentProjectId={e.hero_project_id}
            invalidateKeys={[["email", id], ["emails"], ["calendar"]]}
          />
          <BookingModal
            open={bookOpen}
            onClose={() => { setBookOpen(false); setBlockerSlot(null) }}
            emailId={Number(id)}
            proposedSlot={e.proposed_slot}
            prefillSlot={blockerSlot}
            defaultTitle={e.hero_contact_name || e.subject || "Termin"}
            tenantSlug={e.tenant_slug}
            heroContactId={e.hero_contact_id}
            // 🔴 Ohne den Namen zeigt der Dialog nur „Kontakt: #6672117"
            // (gesehen 23.08.2026 auf dev). `EmailsList` gab ihn laengst mit,
            // diese Seite nicht — zwei Aufrufer desselben Dialogs, einer
            // vollstaendig, einer nicht. Dieselbe Klasse wie #14 und #41.
            heroContactName={e.hero_contact_name}
            heroProjectId={e.hero_project_id}
            invalidateKeys={[["email", id], ["emails"], ["calendar"]]}
          />

          {/* Kategorie + Handlungsbedarf */}
          {(e.category || e.handlungsbedarf) && (
            <div className="mt-3 p-3 rounded-md bg-card text-[13px]">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {e.category && <div><strong className="text-muted-foreground">Kategorie:</strong> {e.category}</div>}
                  {/* Als Aufforderung, nicht als Datenfeld (#7). Marlene: „fuer
                      einen Werkstudent waere gut wenn das als Aufforderung iwo
                      steht" — vorher stand hier eine graue Beschriftung wie bei
                      „Kategorie", und genau so wurde es auch gelesen. */}
                  {e.handlungsbedarf && (
                    <div className="mt-2 flex items-start gap-2 rounded-md border-l-[3px] border-l-amber-500 bg-amber-500/10 px-3 py-2">
                      <ClipboardCheck className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                          Zu tun
                        </div>
                        <div className="mt-0.5 leading-relaxed">{e.handlungsbedarf}</div>
                      </div>
                    </div>
                  )}
                </div>
                <HistoryContextBadge emailId={Number(id)} />
              </div>
            </div>
          )}
        </div>

        {/* Body — HTML in sandboxed iframe (echtes Newsletter-Rendering), Plaintext linkified */}
        <div className="p-6">
          {(e as { is_proactive?: boolean }).is_proactive && (
            <div className="ev2-hinweiszeile" style={{ marginBottom: 12 }}>
              <strong>Handlungsempfehlung des Portals</strong> — keine eingegangene E-Mail.
              Der Text unten ist die Begruendung des Portals, nicht ein Schreiben des Kunden.
            </div>
          )}
          {e.inhalt_entfernt_am ? (
            // Datenschutz-Löschjob: Inhalt nach 90 Tagen geleert.
            <InhaltEntferntHinweis
              tenant={e.tenant_slug}
              messageId={e.message_id}
              folder={e.imap_folder}
              postfach={e.postfach}
              postfachVerfuegbar={e.postfach_verfuegbar}
            />
          ) : (
            <EmailBody html={e.body_html || e.body_text || ""} emailId={e.id} />
          )}
          {e.has_attachments && <AttachmentsSection emailId={e.id} tenantSlug={e.tenant_slug}
                                                    hatteAnhaenge />}
        </div>

        {/* Draft-Response (wenn KI-generiert) */}
        {e.draft_response && (
          <div className="px-6 py-5 border-t bg-secondary/50">
            <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              KI-Antwort-Entwurf
            </div>
            <div className="text-[14px] whitespace-pre-wrap leading-relaxed">
              {e.draft_response}
            </div>
          </div>
        )}

        {/* Action-Bar — Weiterleiten, Verschieben, Re-analysieren, Aufgabe */}
        {/* Dieselbe Aufforderung nochmal direkt ueber den Knoepfen: oben liest
            man sie beim Ankommen, hier braucht man sie beim Entscheiden. */}
        {e.handlungsbedarf && (
          <div className="px-6 pt-4 -mb-2 flex items-start gap-2 text-[13px] text-muted-foreground">
            <ClipboardCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
            <span><strong className="text-foreground">Zu tun:</strong> {e.handlungsbedarf}</span>
          </div>
        )}
        <DetailActionBar emailId={e.id} currentFolder={e.imap_folder}
          heroContactId={e.hero_contact_id} heroProjectId={e.hero_project_id} />

        {/* Reply-Form. Bei Kontaktformular-Mails: effective_reply_to hat
            Vorrang (echte Kunden-Email aus dem Body extrahiert). */}
        <div className="px-6 pb-6 border-t pt-4">
          {(e as { effective_reply_to?: string | null }).effective_reply_to && (
            <div className="mb-2 text-[12px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
              💡 Antwort geht an <strong>{(e as { effective_reply_to?: string | null }).effective_reply_to}</strong>
              {" "}— aus dem Mail-Body extrahiert (Kontaktformular).
              {(e as { effective_reply_to_reason?: string | null }).effective_reply_to_reason && (
                <span className="text-blue-500 ml-1">
                  ({(e as { effective_reply_to_reason?: string | null }).effective_reply_to_reason})
                </span>
              )}
            </div>
          )}
          <ReplyForm
            key={e.id}
            emailId={e.id}
            // Server-Entwurf mitgeben (#131-Pruefung, 27.09.2026): ohne ihn
            // zeigte diese Seite nur die lokale Kopie, und die wird nach jedem
            // erfolgreichen Speichern geloescht.
            manualDraft={{
              to: e.manual_draft_to,
              cc: e.manual_draft_cc,
              subject: e.manual_draft_subject,
              body: e.manual_draft_body,
              saved_at: e.manual_draft_saved_at,
            }}
            defaultTo={
              (e as { effective_reply_to?: string | null }).effective_reply_to
              || e.sender_email
              || e.sender
            }
            defaultSubject={e.subject || ""}
            aiDraft={e.draft_response}
            onGesendet={async (text) => {
              // Nur fragen, wenn sich ueberhaupt buchen laesst — ohne
              // verknuepften Kontakt oder Vorgang fuehrt die Nachfrage in ein
              // Fenster, das nicht buchen kann.
              if (!e.hero_contact_id && !e.hero_project_id) return
              if (e.hero_booking_id) return
              const iso = await terminBlockerFragen(e.id, text)
              if (!iso) return
              setBlockerSlot(iso)
              setBookOpen(true)
            }}
          />
          <ForwardForm
            emailId={e.id}
            defaultSubject={e.subject || ""}
            defaultBody={e.body_text ? `\n\n--- Original-Nachricht ---\nVon: ${e.sender}\nDatum: ${e.received_at}\nBetreff: ${e.subject}\n\n${stripAttachmentDumps(e.body_text).slice(0, 5000)}` : ""}
          />
        </div>
      </div>
    </div>
  )
}

function DetailActionBar({ emailId, currentFolder, heroContactId, heroProjectId }: {
  emailId: number
  currentFolder?: string | null
  /** Ticket #22: der Server faellt fuer die Verknuepfung auf genau diese
   *  Werte der Mail zurueck (`_attachments.py`, „Payload-Wert > Mail-Default").
   *  Hier wird nichts ausgewaehlt — sind beide leer, haengt die Aufgabe an nichts. */
  heroContactId?: number | null
  heroProjectId?: number | null
}) {
  const qc = useQueryClient()
  const [moveOpen, setMoveOpen] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const move = useMutation({
    mutationFn: async (folder: string) => {
      const res = await fetch(`/api/v1/emails/${emailId}/move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ folder }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      qc.invalidateQueries({ queryKey: ["emails"] })
      setMoveOpen(false)
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  const reanalyze = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/v1/emails/${emailId}/reanalyze`, { method: "POST", credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const j = await res.json().catch(() => null) as { category?: string | null } | null
      return j
    },
    onSuccess: (j) => {
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      if (j?.category === "Analyse fehlgeschlagen") {
        toast("KI-Analyse lieferte ungueltiges JSON — Mail bleibt im Review mit Marker.", "error")
      } else {
        toast("KI-Analyse aktualisiert.", "success")
      }
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Reanalyze fehlgeschlagen: " + msg, "error")
    },
  })

  const createTask = useMutation({
    mutationFn: async () => {
      const title = window.prompt("Aufgaben-Titel?")
      if (!title) return
      // #149: dieser Schnellweg schickt keinen Empfaenger mit — die Aufgabe
      // steht in HERO dann bei niemandem. Hinweis, kein Verbot.
      if (!(await darfAufgabeAnlegen({ mitarbeiter: null, contactId: heroContactId, projectId: heroProjectId }))) return
      const res = await fetch(`/api/v1/emails/${emailId}/create-task`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      alert("Aufgabe angelegt")
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  const folders = ["INBOX", "Archive", "Trash", "Sent"]

  return (
    <div className="px-6 py-3 border-t bg-secondary/30 flex items-center gap-2 flex-wrap">
      <div style={{ position: "relative" }}>
        <button className="pp-btn pp-btn-sm" onClick={() => setMoveOpen(!moveOpen)}>
          📁 Verschieben ▾
        </button>
        {moveOpen && (
          <div style={{
            position: "absolute", top: "100%", left: 0, marginTop: 4,
            background: "var(--bg-card)", border: "1px solid var(--border-color)",
            borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            minWidth: 140, zIndex: 100,
          }}>
            {folders.filter((f) => f !== (currentFolder || "INBOX")).map((f) => (
              <button
                key={f}
                onClick={() => move.mutate(f)}
                disabled={move.isPending}
                style={{
                  width: "100%", textAlign: "left", padding: "8px 12px",
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 12.5, display: "block",
                }}
              >→ {f}</button>
            ))}
          </div>
        )}
      </div>
      <button
        className="pp-btn pp-btn-sm"
        onClick={() => reanalyze.mutate()}
        disabled={reanalyze.isPending}
        title="KI-Analyse neu laufen lassen"
      >
        <RefreshCw className="w-3 h-3" /> Re-analysieren
      </button>
      <button
        className="pp-btn pp-btn-sm"
        onClick={() => createTask.mutate()}
        disabled={createTask.isPending}
      >
        ✓ Aufgabe
      </button>
      {err && <span className="text-[11.5px] text-destructive">{err}</span>}
    </div>
  )
}

